import React, { useState, useEffect } from "react";
import { AlertTriangle, CheckCircle } from "lucide-react";
import {
  fetchDisclosureAck,
  writeDisclosureAck,
  getCurrentUid,
} from "../../services/disclosureService";

/**
 * Risk Assessment Disclaimer + Acknowledgment Checkbox
 *
 * Compliance-focused block shown at the bottom of every risk assessment
 * report. Six required disclosure sections, then an acknowledgment record.
 *
 * Persistence: Firestore (was previously localStorage). Acknowledgments
 * are stored under users/{uid}/settings/riskAssessmentDisclosureAck. The
 * DisclaimerGate handles the upstream version of this — in practice
 * users always already-acknowledged before they see this block, so this
 * shows the "Disclaimer acknowledged on [date]" state.
 *
 * To force re-acknowledgment after a material copy change, bump
 * DISCLOSURE_VERSIONS.riskAssessment in disclosureService.js.
 */

const KIND = "riskAssessment";

const Section = ({ title, children }) => (
  <div className="mb-4">
    <h5 className="font-bold text-arc-ink text-sm sm:text-base mb-1.5">
      {title}
    </h5>
    <p className="text-xs sm:text-sm text-arc-muted leading-relaxed">
      {children}
    </p>
  </div>
);

const RiskAssessmentDisclaimer = () => {
  const [ack, setAck] = useState(null); // null | { acknowledgedAt: Timestamp }
  const [saving, setSaving] = useState(false);

  // Load acknowledgment from Firestore on mount.
  useEffect(() => {
    let cancelled = false;
    const uid = getCurrentUid();
    if (!uid) return;
    fetchDisclosureAck(uid, KIND).then((record) => {
      if (!cancelled && record) setAck(record);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fallback acknowledgment path (only fires if a user somehow reached
  // the report without going through the gate). Writes to Firestore.
  const handleAcknowledge = async (e) => {
    if (!e.target.checked || saving) return;
    const uid = getCurrentUid();
    if (!uid) return;
    setSaving(true);
    try {
      const record = await writeDisclosureAck(uid, KIND, [
        "intro", "is", "isnot", "risk", "past", "yours",
      ]);
      setAck(record);
    } catch (_) {
      // UI silently rolls back the check; user can retry.
    } finally {
      setSaving(false);
    }
  };

  const acknowledged = !!ack?.acknowledgedAt;
  // Firestore serverTimestamps come back as Timestamp objects or null
  // (briefly null between local write and server confirmation). Handle both.
  const ackDateRaw = ack?.acknowledgedAt;
  const ackDate = acknowledged
    ? (() => {
        try {
          if (ackDateRaw?.toDate) return ackDateRaw.toDate();
          if (typeof ackDateRaw === "string") return new Date(ackDateRaw);
          return new Date();
        } catch (_) {
          return new Date();
        }
      })().toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <div className="border-t border-arc-line pt-4 sm:pt-6 mt-6">
      <div className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/5 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={18} className="text-amber-400 flex-shrink-0" />
          <h4 className="eyebrow text-amber-400">
            Important Disclosure · Please Read
          </h4>
        </div>

        <h3 className="font-bold text-lg sm:text-xl text-arc-ink mb-4">
          Educational Use Only — Not Investment Advice
        </h3>

        <Section title="">
          The ARC FIRE Tracker and the risk assessment report it generates are
          educational tools, not investment advice. ARCrypto is not a registered
          investment advisor, broker-dealer, financial planner, or tax advisor.
          Nothing in this report constitutes a recommendation to buy, sell, or
          hold any specific asset.
        </Section>

        <Section title="What this report is.">
          Based on your answers, the report shows a hypothetical allocation
          framework — a starting point for your own research and your own
          conversations with licensed professionals. Different people with the
          same risk score may reach different conclusions, and that's the point.
        </Section>

        <Section title="What this report is not.">
          It is not a personalized investment plan, a portfolio prescription,
          or a guaranteed roadmap. It does not account for your full financial
          picture — taxes, debts, dependents, employment risk, insurance, real
          estate, estate planning, or your local regulatory environment.
        </Section>

        <Section title="Risk of loss.">
          All investing involves risk, including loss of principal.
          Cryptocurrency can lose 50–90% of its value in short periods and has
          historically gone through multi-year drawdowns. Leveraged and on-chain
          DeFi positions can lose 100% of their value in days. Equities and
          precious metals can also experience significant declines. Some assets
          shown in this app are illiquid and may not be sellable at the price
          displayed.
        </Section>

        <Section title="Past performance does not guarantee future results.">
          Any historical returns shown anywhere in this app — for managed
          vaults, stock signals, precious metals, or benchmark indices — are not
          a promise of future performance. Returns shown may include
          hypothetical or backtested data, which has known limitations: it does
          not reflect actual trading conditions, liquidity, slippage, fees,
          taxes, or the emotional discipline required to hold a strategy
          through drawdowns.
        </Section>

        <Section title="Your decisions are yours.">
          You alone are responsible for your investment decisions. Before acting
          on anything you see here, consult a licensed investment advisor, tax
          professional, or attorney for advice specific to your situation.
        </Section>

        {/* Acknowledgment checkbox */}
        <div className="mt-5 pt-5 border-t border-amber-500/20">
          {acknowledged ? (
            <div className="flex items-start gap-3 rounded-xl bg-emerald-500/5 border border-emerald-500/30 p-3">
              <CheckCircle
                size={18}
                className="text-emerald-400 flex-shrink-0 mt-0.5"
              />
              <div className="text-xs sm:text-sm">
                <p className="text-emerald-300 font-semibold">
                  Disclaimer acknowledged
                </p>
                <p className="text-emerald-200/80 mt-0.5">
                  You confirmed you read and understood the disclosure on{" "}
                  {ackDate}. This record is stored locally on this device.
                </p>
              </div>
            </div>
          ) : (
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                onChange={handleAcknowledge}
                className="mt-1 w-4 h-4 flex-shrink-0 accent-arc-gold cursor-pointer"
              />
              <span className="text-xs sm:text-sm text-arc-ink leading-relaxed group-hover:text-arc-light transition-colors">
                <span className="font-semibold">
                  I have read and understood all six sections of this disclosure
                  above.
                </span>{" "}
                I acknowledge that the strategy shown is for educational
                purposes only, is not personalized investment advice, and that I
                alone am responsible for any decisions I make as a result of
                using this tool.
              </span>
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

export default RiskAssessmentDisclaimer;
